'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Slide {
  id: string;
  title: string;
  subtitle: string;
  speakerNotes: string;
}

const SLIDES: Slide[] = [
  {
    id: 'cover',
    title: 'Cover — Wings Bharath',
    subtitle: 'Company Overview Deck · 2026',
    speakerNotes:
      'Welcome the client. Introduce Wings Bharath as India\'s precision-first aerospace manufacturer. Highlight that we are an Airbus-approved, defence-certified supplier with active programs across commercial aviation, defence, space, and robotics. Set the tone: this is a mission-critical partner, not a generic vendor.',
  },
  {
    id: 'overview',
    title: 'Company Overview',
    subtitle: 'Precision at the Speed of Flight',
    speakerNotes:
      'Emphasise our Airbus-approved status and defence certifications. Key talking points: 200+ commercial projects, 30+ defence platforms, 10+ space programs, 20+ material grades. Stress full traceability and approved supplier status. Mention that every part is built to mission-critical tolerances — this differentiates us from tier-2 suppliers.',
  },
  {
    id: 'sectors',
    title: 'Sectors We Serve',
    subtitle: 'Four Verticals. One Standard.',
    speakerNotes:
      'Walk through all four verticals: Commercial Aviation (Airbus A320, Rafale), Defence (LCA Tejas, Su-30 MKI, UAVs), Space (ISRO satellite frames, launch vehicles), and Robotics & Automation. Stress that the same quality standard applies across all four — no compromise regardless of sector. Ask the client which vertical is most relevant to their program.',
  },
  {
    id: 'programs',
    title: 'Key Programs',
    subtitle: 'Programs We Power',
    speakerNotes:
      'Highlight the six flagship programs: Airbus A320, Rafale (Class 2 & 3), LCA Tejas, Sukhoi Su-30 MKI, UAV Programs, and Satellite/Launch Vehicles. For defence clients, focus on LCA Tejas fuselage assemblies and Su-30 MKI structural components. For commercial clients, lead with Airbus A320 and Rafale approvals. These are not aspirational — these are active, approved programs.',
  },
  {
    id: 'capabilities',
    title: 'Manufacturing Capabilities',
    subtitle: 'Built to Aerospace Spec',
    speakerNotes:
      'Cover the four core capabilities: CNC Precision Machining, Sheet Metal Fabrication, Structural Assemblies, and Reverse Engineering. Highlight the 20+ material grades — Al 2024/6061/7075, SS 304/316/17-4PH, Inconel, Titanium Ti-6Al-4V. Mention the digital platform: real-time project tracking, digital quality reports, client portal, and AI-integrated RFQ. This is a differentiator — most manufacturers don\'t offer this level of digital transparency.',
  },
  {
    id: 'certifications',
    title: 'Certifications & Approvals',
    subtitle: 'Certified. Approved. Trusted.',
    speakerNotes:
      'Lead with the four headline approvals: Airbus Approved Supplier (A320), Rafale Approved (Class 2 & 3), Defence Certified (LCA Tejas, Su-30 MKI), Space Grade. Then cover quality standards: AS9100D, ISO 9001:2015, ITAR Compliant, NADCAP. Emphasise that every approval was earned through rigorous audits — not self-declared. This is the trust foundation for any long-term partnership.',
  },
  {
    id: 'stats',
    title: 'Performance Metrics',
    subtitle: 'By the Numbers',
    speakerNotes:
      'Present the headline metrics: 200+ commercial projects, 30+ defence platforms, 10+ space programs, 20+ material grades, 99.97% quality success rate, 98.5% on-time delivery, 15+ years of excellence, 50+ active partnerships. These are not estimates — they reflect real program history. The 99.97% quality rate is particularly powerful for clients evaluating supply chain risk.',
  },
  {
    id: 'contact',
    title: 'Engage With Us',
    subtitle: 'Ready to Build Mission-Critical?',
    speakerNotes:
      'Close with a clear call to action. Invite the client to submit an RFQ directly from the platform — it\'s AI-integrated and fast. Offer to walk them through the client portal for real-time project visibility. Reiterate the three pillars: Precision Engineering, Mission-Critical Commitment, Digital Manufacturing. Ask: "Which program would you like to start with?" — make it easy to say yes.',
  },
];

interface PresenterModeProps {
  onClose: () => void;
  initialSlideIndex?: number;
}

export default function PresenterMode({ onClose, initialSlideIndex = 0 }: PresenterModeProps) {
  const [currentIndex, setCurrentIndex] = useState(initialSlideIndex);
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(true);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const current = SLIDES[currentIndex];
  const total = SLIDES.length;

  // Timer
  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(total - 1, index));
      setCurrentIndex(clamped);
      // Navigate the iframe to the target slide
      if (iframeRef.current) {
        try {
          iframeRef.current.contentWindow?.location.replace(`/company-deck#${SLIDES[clamped].id}`);
        } catch {
          iframeRef.current.src = `/company-deck#${SLIDES[clamped].id}`;
        }
      }
    },
    [total]
  );

  const prev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);
  const next = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, onClose]);

  const progressPct = ((currentIndex + 1) / total) * 100;

  return (
    <div className="fixed inset-0 z-[9999] bg-[#010a10] flex flex-col" style={{ fontFamily: 'inherit' }}>
      {/* ── TOP BAR ── */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#020c14] border-b border-[#0d2233] flex-shrink-0">
        {/* Left: branding + slide info */}
        <div className="flex items-center space-x-4 min-w-0">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse" />
            <span className="font-mono text-[10px] text-[#00d4ff] tracking-widest uppercase hidden sm:block">
              Presenter Mode
            </span>
          </div>
          <div className="w-px h-5 bg-[#0d2233]" />
          <div className="min-w-0">
            <p className="font-mono text-xs text-white font-semibold truncate">{current.title}</p>
            <p className="font-mono text-[10px] text-[#4a7a8a] truncate">{current.subtitle}</p>
          </div>
        </div>

        {/* Center: slide counter */}
        <div className="flex items-center space-x-3">
          <button
            onClick={prev}
            disabled={currentIndex === 0}
            className="w-8 h-8 rounded-lg bg-[#0d2233] border border-[#1a3a4a] flex items-center justify-center text-[#4a7a8a] hover:text-[#00d4ff] hover:border-[#00d4ff]/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
          >
            <Icon name="ChevronLeftIcon" size={14} />
          </button>
          <div className="flex items-center space-x-1.5 bg-[#0d2233] border border-[#1a3a4a] rounded-lg px-3 py-1.5">
            <span className="font-mono text-sm text-[#00d4ff] font-bold">{String(currentIndex + 1).padStart(2, '0')}</span>
            <span className="font-mono text-xs text-[#1a3a4a]">/</span>
            <span className="font-mono text-sm text-[#4a7a8a]">{String(total).padStart(2, '0')}</span>
          </div>
          <button
            onClick={next}
            disabled={currentIndex === total - 1}
            className="w-8 h-8 rounded-lg bg-[#0d2233] border border-[#1a3a4a] flex items-center justify-center text-[#4a7a8a] hover:text-[#00d4ff] hover:border-[#00d4ff]/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
          >
            <Icon name="ChevronRightIcon" size={14} />
          </button>
        </div>

        {/* Right: timer + controls */}
        <div className="flex items-center space-x-3">
          {/* Timer */}
          <div className="flex items-center space-x-2 bg-[#0d2233] border border-[#1a3a4a] rounded-lg px-3 py-1.5">
            <Icon name="ClockIcon" size={13} className="text-[#4a7a8a]" />
            <span className="font-mono text-sm text-white font-bold tabular-nums">{formatTime(elapsed)}</span>
            <button
              onClick={() => setRunning((r) => !r)}
              className="ml-1 text-[#4a7a8a] hover:text-[#00d4ff] transition-colors"
              title={running ? 'Pause timer' : 'Resume timer'}
            >
              <Icon name={running ? 'PauseIcon' : 'PlayIcon'} size={12} />
            </button>
            <button
              onClick={() => { setElapsed(0); setRunning(true); }}
              className="text-[#4a7a8a] hover:text-[#ee9572] transition-colors"
              title="Reset timer"
            >
              <Icon name="ArrowPathIcon" size={12} />
            </button>
          </div>

          {/* Notes toggle */}
          <button
            onClick={() => setNotesExpanded((n) => !n)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border text-xs font-mono transition-all duration-200 ${
              notesExpanded
                ? 'bg-[#00d4ff]/10 border-[#00d4ff]/40 text-[#00d4ff]'
                : 'bg-[#0d2233] border-[#1a3a4a] text-[#4a7a8a] hover:text-[#00d4ff]'
            }`}
            title="Toggle speaker notes"
          >
            <Icon name="ChatBubbleBottomCenterTextIcon" size={13} />
            <span className="hidden sm:block">Notes</span>
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-[#0d2233] border border-[#1a3a4a] flex items-center justify-center text-[#4a7a8a] hover:text-red-400 hover:border-red-400/50 transition-all duration-200"
            title="Exit presenter mode (Esc)"
          >
            <Icon name="XMarkIcon" size={14} />
          </button>
        </div>
      </div>

      {/* ── PROGRESS BAR ── */}
      <div className="h-0.5 bg-[#0d2233] flex-shrink-0">
        <div
          className="h-full bg-gradient-to-r from-[#00d4ff] to-[#ee9572] transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* ── MAIN AREA ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Slide preview (iframe) */}
        <div className={`flex flex-col flex-1 min-w-0 transition-all duration-300 ${notesExpanded ? 'lg:w-[65%]' : 'w-full'}`}>
          <div className="flex-1 relative bg-[#010a10] overflow-hidden">
            <iframe
              ref={iframeRef}
              src={`/company-deck#${current.id}`}
              className="absolute inset-0 w-full h-full border-0"
              title="Slide Preview"
              style={{ pointerEvents: 'none' }}
            />
            {/* Overlay to prevent iframe interaction & show nav hints */}
            <div className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none">
              <div
                className={`w-12 h-12 rounded-full bg-[#020c14]/80 border border-[#0d2233] flex items-center justify-center pointer-events-auto cursor-pointer transition-opacity duration-200 ${
                  currentIndex === 0 ? 'opacity-0' : 'opacity-60 hover:opacity-100'
                }`}
                onClick={prev}
              >
                <Icon name="ChevronLeftIcon" size={20} className="text-[#00d4ff]" />
              </div>
              <div
                className={`w-12 h-12 rounded-full bg-[#020c14]/80 border border-[#0d2233] flex items-center justify-center pointer-events-auto cursor-pointer transition-opacity duration-200 ${
                  currentIndex === total - 1 ? 'opacity-0' : 'opacity-60 hover:opacity-100'
                }`}
                onClick={next}
              >
                <Icon name="ChevronRightIcon" size={20} className="text-[#00d4ff]" />
              </div>
            </div>
          </div>

          {/* Slide strip */}
          <div className="flex-shrink-0 bg-[#020c14] border-t border-[#0d2233] px-4 py-3 overflow-x-auto">
            <div className="flex space-x-2 min-w-max">
              {SLIDES.map((slide, idx) => (
                <button
                  key={slide.id}
                  onClick={() => goTo(idx)}
                  className={`flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-lg border transition-all duration-200 ${
                    idx === currentIndex
                      ? 'bg-[#00d4ff]/10 border-[#00d4ff]/50 text-[#00d4ff]'
                      : 'bg-[#0d2233] border-[#1a3a4a] text-[#4a7a8a] hover:border-[#00d4ff]/30 hover:text-white'
                  }`}
                >
                  <span className="font-mono text-[10px] font-bold">{String(idx + 1).padStart(2, '0')}</span>
                  <span className="font-mono text-[9px] mt-0.5 max-w-[72px] truncate">{slide.id.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Speaker Notes Panel */}
        {notesExpanded && (
          <div className="hidden lg:flex flex-col w-[35%] flex-shrink-0 bg-[#020c14] border-l border-[#0d2233] overflow-hidden">
            {/* Notes header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#0d2233] flex-shrink-0">
              <div className="flex items-center space-x-2">
                <Icon name="ChatBubbleBottomCenterTextIcon" size={14} className="text-[#00d4ff]" />
                <span className="font-mono text-xs text-[#00d4ff] tracking-widest uppercase">Speaker Notes</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-mono text-[10px] text-[#4a7a8a]">Slide {currentIndex + 1} of {total}</span>
              </div>
            </div>

            {/* Slide title in notes */}
            <div className="px-5 pt-5 pb-3 flex-shrink-0 border-b border-[#0d2233]/50">
              <p className="font-mono text-[10px] text-[#4a7a8a] tracking-widest uppercase mb-1">Current Slide</p>
              <h3 className="text-white font-semibold text-sm leading-snug">{current.title}</h3>
              <p className="text-[#4a7a8a] text-xs mt-0.5">{current.subtitle}</p>
            </div>

            {/* Notes content */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <p className="font-mono text-[10px] text-[#4a7a8a] tracking-widest uppercase mb-3">Talking Points</p>
              <p className="text-[#c8d8e0] text-sm leading-relaxed">{current.speakerNotes}</p>
            </div>

            {/* Next slide preview */}
            {currentIndex < total - 1 && (
              <div className="flex-shrink-0 border-t border-[#0d2233] px-5 py-4 bg-[#010a10]">
                <p className="font-mono text-[10px] text-[#4a7a8a] tracking-widest uppercase mb-2">Up Next</p>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-[#0d2233] border border-[#1a3a4a] flex items-center justify-center flex-shrink-0">
                    <span className="font-mono text-xs text-[#00d4ff] font-bold">{String(currentIndex + 2).padStart(2, '0')}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-xs font-semibold truncate">{SLIDES[currentIndex + 1].title}</p>
                    <p className="text-[#4a7a8a] text-[10px] truncate">{SLIDES[currentIndex + 1].subtitle}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Keyboard shortcuts */}
            <div className="flex-shrink-0 border-t border-[#0d2233] px-5 py-3 bg-[#010a10]">
              <p className="font-mono text-[10px] text-[#4a7a8a] tracking-widest uppercase mb-2">Keyboard Shortcuts</p>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { key: '→ / Space', action: 'Next slide' },
                  { key: '←', action: 'Prev slide' },
                  { key: 'Esc', action: 'Exit mode' },
                ].map((s) => (
                  <div key={s.key} className="flex items-center space-x-2">
                    <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#0d2233] border border-[#1a3a4a] rounded text-[#00d4ff]">
                      {s.key}
                    </span>
                    <span className="font-mono text-[9px] text-[#4a7a8a]">{s.action}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
